/**
 * bankImport/dedup — Deterministische Dedup-Doc-IDs für `bank_transactions`
 * (architecture-bank-reconciliation.md §6.4).
 *
 * `fnv1a64` ist SYNCHRON und dependency-frei via BigInt — bewusst KEIN
 * `crypto.subtle` (Web Crypto SHA-256 ist in CRA-5/jest-jsdom nicht
 * verfügbar, der Determinismus-Test würde im CI scheitern). Dedup braucht
 * keine kryptographische Kollisionssicherheit, nur Determinismus + Streuung.
 *
 * @module bankImport/dedup
 */

import type { ParsedStatementRow } from './types';

// BigInt-Konstanten via `BigInt(...)`-Aufruf statt `123n`-Literalsyntax:
// Letztere erfordert TS --target ES2020+ (TS2737), dieses Projekt kompiliert
// mit target "es5" (tsconfig.json). Laufzeit-Verhalten ist identisch.
const FNV_OFFSET_BASIS_64 = BigInt('14695981039346656037');
const FNV_PRIME_64 = BigInt('1099511628211');
const MASK_64 = BigInt('0xffffffffffffffff');

/** ASCII Unit Separator (0x1F) — Feldtrenner der kanonischen Hash-Vorstufe (§6.4). */
const FIELD_SEPARATOR = '\x1f';

/**
 * FNV-1a, 64 Bit, synchron. Iteriert über die UTF-8-Bytes von `s`.
 *
 * Kanonische Testvektoren (architecture §6.4, BR-1-DoD — PFLICHT):
 * `fnv1a64('') === 'cbf29ce484222325'`, `fnv1a64('a') === 'af63dc4c8601ec8c'`.
 *
 * @param s - Beliebiger UTF-8-fähiger String.
 * @returns 16 Hex-Zeichen, zero-padded, lowercase.
 */
export function fnv1a64(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let hash = FNV_OFFSET_BASIS_64;
  for (let i = 0; i < bytes.length; i += 1) {
    hash ^= BigInt(bytes[i]);
    hash = (hash * FNV_PRIME_64) & MASK_64;
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * Normalisierung für die Dedup-Kanonisierung: Uppercase, alles außer
 * `[A-Z0-9]` entfernen. Identischer Algorithmus wie `normalizeRef` in
 * `bankMatching/normalize.ts` (§8.1) — hier bewusst LOKAL dupliziert statt
 * importiert: BR-1 (`bankImport/`) darf laut modules.md-Abhängigkeitsgraph
 * NICHT von BR-4/5 (`bankMatching/`) abhängen (umgekehrt: BR-4 hängt nur von
 * BR-0 ab, „NICHT BR-3"). Ein Cross-Import in die andere Richtung würde die
 * Modul-Grenze verletzen und mit dem zeitgleich arbeitenden bankMatching-
 * Coder kollidieren. Bei einer künftigen Änderung des Algorithmus MUSS diese
 * Kopie synchron nachgezogen werden (siehe changelog-parser.md).
 */
function normalizeRefForDedup(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function buildCanonicalString(accountId: string, row: ParsedStatementRow, occurrenceIndex: number): string {
  return [
    accountId,
    row.bookingDate,
    String(row.amountCents),
    normalizeRefForDedup(row.purpose).slice(0, 140),
    row.counterpartyIban ?? '',
    row.acctSvcrRef ?? row.endToEndId ?? '',
    String(occurrenceIndex),
  ].join(FIELD_SEPARATOR);
}

/**
 * Deterministische `bank_transactions`-Doc-ID: Datums-Präfix (menschenlesbar,
 * verkleinert den Kollisionsraum auf „pro Konto pro Buchungstag") +
 * `fnv1a64` der kanonischen Feld-Kombination (§6.4).
 *
 * @param accountId - Ziel-`bank_accounts`-Doc-ID (trennt den Kollisionsraum pro Konto/User).
 * @param row - Die geparste Zeile.
 * @param occurrenceIndex - Laufindex identischer Zeilen INNERHALB derselben Datei
 *   (`assignOccurrenceIndexes`) — hält zwei echte Duplikate am selben Tag auseinander.
 * @returns z.B. `'20260315-af63dc4c8601ec8c'`.
 */
export function computeTxDocId(accountId: string, row: ParsedStatementRow, occurrenceIndex: number): string {
  const datePrefix = row.bookingDate.replace(/-/g, '');
  return `${datePrefix}-${fnv1a64(buildCanonicalString(accountId, row, occurrenceIndex))}`;
}

/**
 * Weist jeder Zeile einer Datei einen `occurrenceIndex` zu: 0, 1, 2, … für
 * mehrere fachlich identische Zeilen (gleicher Tag/Betrag/Zweck/Gegenpartei/
 * Referenz) INNERHALB derselben Datei. Dadurch bekommen zwei echte
 * Duplikat-Buchungen unterschiedliche Dedup-IDs (§6.4), während ein
 * wiederholter Import DERSELBEN Datei (gleiche Reihenfolge) wieder dieselben
 * IDs erzeugt (Determinismus — BR-1-DoD).
 *
 * `accountId` fließt bewusst NICHT in den Identitäts-Schlüssel dieser
 * Funktion ein (nicht Teil der Signatur) — innerhalb eines Parse-Laufs ist
 * das Konto ohnehin konstant; `computeTxDocId` ergänzt `accountId` separat.
 *
 * @param rows - Alle Zeilen EINER geparsten Datei, in Datei-Reihenfolge.
 * @returns Zeilen mit zugewiesenem `occurrenceIndex`, gleiche Reihenfolge wie `rows`.
 */
export function assignOccurrenceIndexes(
  rows: ParsedStatementRow[]
): Array<{ row: ParsedStatementRow; occurrenceIndex: number }> {
  const seenCounts = new Map<string, number>();
  return rows.map((row) => {
    const identityKey = [
      row.bookingDate,
      String(row.amountCents),
      normalizeRefForDedup(row.purpose).slice(0, 140),
      row.counterpartyIban ?? '',
      row.acctSvcrRef ?? row.endToEndId ?? '',
    ].join(FIELD_SEPARATOR);
    const occurrenceIndex = seenCounts.get(identityKey) ?? 0;
    seenCounts.set(identityKey, occurrenceIndex + 1);
    return { row, occurrenceIndex };
  });
}
